import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFolders, getFiles, deleteFile, deleteFolder, detectIdenticalScreenshots } from '@/api/s3File';
import { useSearchParams } from 'react-router-dom';
import { getUsers } from '@/api/user';
import CopyButton from '@/components/ui/copyButton';
import { EyeIcon, LogOut, Clock, Trash2, Ticket, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Header } from '@/components/layout/header';
import { useAuthStore } from '@/stores/auth';
import { Main } from '@/components/layout/main';
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createTicket } from '@/api/ticket';
import { polishContent } from '@/api/openai';
import toastify from '@/utils/toastify';

const S3File: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentFolder = searchParams.get('folder') || '';
    const [showGallery, setShowGallery] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [fileToDelete, setFileToDelete] = useState<string | null>(null);
    const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
    const [confirmFolderName, setConfirmFolderName] = useState('');
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<{ key: string; url: string } | null>(null);
    const [reason, setReason] = useState('');
    const [isPolishing, setIsPolishing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showIdenticalModal, setShowIdenticalModal] = useState(false);
    const [identicalResults, setIdenticalResults] = useState<{
        groups: Array<{
            hash: string;
            files: Array<{
                key: string;
                url: string;
                lastModified: string;
            }>;
        }>;
        totalIdenticalGroups: number;
        totalDuplicateFiles: number;
    } | null>(null);
    const [isDetecting, setIsDetecting] = useState(false);
    const [selectedScreenshots, setSelectedScreenshots] = useState<Set<string>>(new Set());
    const [showTicketModalFromIdentical, setShowTicketModalFromIdentical] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [showTicketModalFromFiles, setShowTicketModalFromFiles] = useState(false);
    const [shouldReopenIdenticalModal, setShouldReopenIdenticalModal] = useState(false);
    const queryClient = useQueryClient();

    const { logout } = useAuthStore();

    const { data: usersData } = useQuery({
        queryKey: ['users'],
        queryFn: () => getUsers({ page: 1, limit: -1 }),
    });

    const { data: folders = [], isLoading: loadingFoldes } = useQuery({
        queryKey: ['folders', currentFolder],
        queryFn: () => getFolders({ prefix: currentFolder === '/' ? '' : currentFolder }),
    });

    const { data: files = [] } = useQuery({
        queryKey: ['files', currentFolder],
        queryFn: () => getFiles({ prefix: currentFolder === '/' ? '' : currentFolder }),
    });

    // Clear file selection and identical results when folder changes
    useEffect(() => {
        setSelectedFiles(new Set());
        setIdenticalResults(null);
        setSelectedScreenshots(new Set());
    }, [currentFolder]);

    useEffect(() => {
        if (!showGallery && shouldReopenIdenticalModal) {
            setShowIdenticalModal(true);
            setShouldReopenIdenticalModal(false);
        }
    }, [showGallery, shouldReopenIdenticalModal]);

    const sortedFiles = [...files].sort((a, b) =>
        new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
    );

    // Check if current folder is a user's screenshots folder (pattern: screenshot/<date>/<userId>/)
    const isUserScreenshotsFolder = currentFolder.startsWith('screenshot/') && 
        currentFolder.split('/').filter(Boolean).length === 3;

    const handleDetectIdenticalScreenshots = async () => {
        if (!currentFolder) {
            toastify.error('No folder selected');
            return;
        }

        // If results already exist for this folder, just open the modal
        if (identicalResults) {
            setShowIdenticalModal(true);
            return;
        }

        setIsDetecting(true);
        try {
            const results = await detectIdenticalScreenshots({ prefix: currentFolder });
            setIdenticalResults(results);
            setShowIdenticalModal(true);
            if (results.totalIdenticalGroups > 0) {
                toastify.success(`Found ${results.totalIdenticalGroups} group(s) of identical screenshots with ${results.totalDuplicateFiles} duplicate file(s)`);
            } else {
                toastify.info('No identical screenshots found');
            }
        } catch (error) {
            console.error('Error detecting identical screenshots:', error);
            toastify.error('Failed to detect identical screenshots. Please try again.');
        } finally {
            setIsDetecting(false);
        }
    };

    const handleFolderClick = (folder: string) => {
        setSearchParams({ folder });
    };

    const handleBackClick = () => {
        if (currentFolder) {
            const parentFolder = currentFolder.split('/').slice(0, -2).join('/') + '/';
            setSearchParams({ folder: parentFolder });
        }
    };

    const handleImageClick = (index: number) => {
        setSelectedImageIndex(index);
        setShowGallery(true);
    };

    const handleDeleteClick = (e: React.MouseEvent, fileKey: string) => {
        e.stopPropagation();
        setFileToDelete(fileKey);
    };

    const handleConfirmDelete = async () => {
        if (fileToDelete) {
            try {
                await deleteFile({ key: fileToDelete });
                queryClient.invalidateQueries({ queryKey: ['files', currentFolder] });
                setFileToDelete(null);
            } catch (error) {
                console.error('Error deleting file:', error);
            }
        }
    };

    const handleDeleteFolderClick = (e: React.MouseEvent, folder: string) => {
        e.stopPropagation();
        setFolderToDelete(folder);
        setConfirmFolderName('');
    };

    const handleConfirmDeleteFolder = async () => {
        if (folderToDelete && confirmFolderName === folderToDelete.split('/').slice(-2, -1).join('/')) {
            try {
                await deleteFolder({ key: folderToDelete });
                queryClient.invalidateQueries({ queryKey: ['folders', currentFolder] });
                setFolderToDelete(null);
                setConfirmFolderName('');
            } catch (error) {
                console.error('Error deleting folder:', error);
            }
        }
    };

    const handleCreateTicketClick = (e: React.MouseEvent, file: { key: string; url: string }) => {
        e.stopPropagation();
        setSelectedFile(file);
        setReason('');
        setShowTicketModal(true);
    };

    const handleToggleFileSelection = (url: string) => {
        const newSelected = new Set(selectedFiles);
        if (newSelected.has(url)) {
            newSelected.delete(url);
        } else {
            newSelected.add(url);
        }
        setSelectedFiles(newSelected);
    };

    const handleSelectAllFiles = () => {
        if (selectedFiles.size === sortedFiles.length) {
            setSelectedFiles(new Set());
        } else {
            setSelectedFiles(new Set(sortedFiles.map(f => f.url)));
        }
    };

    const handleCreateTicketFromFiles = () => {
        if (selectedFiles.size === 0) {
            toastify.error('Please select at least one screenshot.');
            return;
        }

        // Extract user ID from current folder path
        // Pattern: screenshot/<date>/<userId>/
        const folderParts = currentFolder.split('/').filter(Boolean);
        const userId = folderParts.length >= 3 ? folderParts[2] : '';

        if (!userId) {
            toastify.error('Could not determine user ID from folder path.');
            return;
        }

        setShowTicketModalFromFiles(true);
    };

    const handleSubmitTicketFromFiles = async () => {
        if (selectedFiles.size === 0 || !reason.trim()) {
            toastify.error('Please select screenshots and enter a reason.');
            return;
        }

        // Extract user ID from current folder path
        const folderParts = currentFolder.split('/').filter(Boolean);
        const userId = folderParts.length >= 3 ? folderParts[2] : '';

        if (!userId) {
            toastify.error('Could not determine user ID from folder path.');
            return;
        }

        setIsSubmitting(true);
        try {
            const imageUrls = Array.from(selectedFiles);
            await createTicket({
                uid: userId,
                urlImages: imageUrls,
                reason: reason.trim(),
            });
            toastify.success(`Ticket created with ${imageUrls.length} screenshot(s) and sent to Slack channel successfully.`);
            setShowTicketModalFromFiles(false);
            setSelectedFiles(new Set());
            setReason('');
        } catch (error) {
            console.error('Error creating ticket:', error);
            toastify.error('Failed to create ticket. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePolishContent = async () => {
        if (!reason.trim()) {
            toastify.error('Please enter a reason before polishing.');
            return;
        }

        setIsPolishing(true);
        try {
            const polished = await polishContent({ text: reason });
            if (polished) {
                setReason(polished);
                toastify.success('Content polished successfully.');
            } else {
                toastify.error('Failed to polish content. Please try again.');
            }
        } catch (error) {
            console.error('Error polishing content:', error);
            toastify.error('Failed to polish content. Please try again.');
        } finally {
            setIsPolishing(false);
        }
    };

    const handleSubmitTicket = async () => {
        if (!selectedFile || !reason.trim()) {
            toastify.error('Please enter a reason before submitting.');
            return;
        }

        // Extract user ID from file path.
        // Key pattern: screenshot/<date>/<userId>/<filename>
        const fileKeyParts = selectedFile.key.split('/').filter(Boolean);
        const userId = fileKeyParts.length >= 3 ? fileKeyParts[2] : '';

        if (!userId) {
            toastify.error('Could not determine user ID from file path.');
            return;
        }

        setIsSubmitting(true);
        try {
            await createTicket({
                uid: userId,
                urlImages: [selectedFile.url],
                reason: reason.trim(),
            });
            toastify.success('Ticket created and sent to Slack channel successfully.');
            setShowTicketModal(false);
            setSelectedFile(null);
            setReason('');
        } catch (error) {
            console.error('Error creating ticket:', error);
            toastify.error('Failed to create ticket. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleScreenshotSelection = (url: string) => {
        const newSelected = new Set(selectedScreenshots);
        if (newSelected.has(url)) {
            newSelected.delete(url);
        } else {
            newSelected.add(url);
        }
        setSelectedScreenshots(newSelected);
    };

    const handleSelectAllInGroup = (groupFiles: Array<{ key: string; url: string; lastModified: string }>) => {
        const newSelected = new Set(selectedScreenshots);
        const groupUrls = groupFiles.map(f => f.url);
        const allSelected = groupUrls.every(url => newSelected.has(url));
        
        if (allSelected) {
            // Deselect all in group
            groupUrls.forEach(url => newSelected.delete(url));
        } else {
            // Select all in group
            groupUrls.forEach(url => newSelected.add(url));
        }
        setSelectedScreenshots(newSelected);
    };

    const getSelectedCountByGroup = () => {
        if (!identicalResults) return {};
        const counts: Record<number, number> = {};
        identicalResults.groups.forEach((group, groupIndex) => {
            const selectedInGroup = group.files.filter(file => selectedScreenshots.has(file.url)).length;
            if (selectedInGroup > 0) {
                counts[groupIndex] = selectedInGroup;
            }
        });
        return counts;
    };

    const calculateGroupDuration = (files: Array<{ lastModified: string }>): string => {
        if (files.length < 2) return '';
        
        const timestamps = files.map(f => new Date(f.lastModified).getTime()).sort((a, b) => a - b);
        const earliest = timestamps[0];
        const latest = timestamps[timestamps.length - 1];
        const durationMs = latest - earliest;
        
        const minutes = Math.floor(durationMs / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        
        if (days > 0) {
            return `${days}d ${hours % 24}h`;
        } else if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else {
            return `${minutes}m`;
        }
    };

    const handleOpenImageFromIdentical = (fileKey: string) => {
        const fileIndexInAll = sortedFiles.findIndex(f => f.key === fileKey);
        if (fileIndexInAll !== -1) {
            setSelectedImageIndex(fileIndexInAll);
            setShouldReopenIdenticalModal(true);
            setShowIdenticalModal(false);
            setShowGallery(true);
        }
    };

    const handleCreateTicketFromIdentical = () => {
        if (selectedScreenshots.size === 0) {
            toastify.error('Please select at least one screenshot.');
            return;
        }

        // Extract user ID from current folder path
        // Pattern: screenshot/<date>/<userId>/
        const folderParts = currentFolder.split('/').filter(Boolean);
        const userId = folderParts.length >= 3 ? folderParts[2] : '';

        if (!userId) {
            toastify.error('Could not determine user ID from folder path.');
            return;
        }

        setShowTicketModalFromIdentical(true);
        setShowIdenticalModal(false);
    };

    const handleSubmitTicketFromIdentical = async () => {
        if (selectedScreenshots.size === 0 || !reason.trim()) {
            toastify.error('Please select screenshots and enter a reason.');
            return;
        }

        // Extract user ID from current folder path
        // Pattern: screenshot/<date>/<userId>/
        const folderParts = currentFolder.split('/').filter(Boolean);
        const userId = folderParts.length >= 3 ? folderParts[2] : '';

        if (!userId) {
            toastify.error('Could not determine user ID from folder path.');
            return;
        }

        setIsSubmitting(true);
        try {
            const imageUrls = Array.from(selectedScreenshots);
            await createTicket({
                uid: userId,
                urlImages: imageUrls,
                reason: reason.trim(),
            });
            toastify.success(`Ticket created with ${imageUrls.length} screenshot(s) and sent to Slack channel successfully.`);
            setShowTicketModalFromIdentical(false);
            setSelectedScreenshots(new Set());
            setReason('');
            // Keep identicalResults in state so admin can create more tickets if needed
        } catch (error) {
            console.error('Error creating ticket:', error);
            toastify.error('Failed to create ticket. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const images = sortedFiles.map(file => ({
        original: file.url,
        thumbnail: file.url,
        description: file.key
    }));

    return (
        <>
            <Header fixed className='flex justify-between w-[inherit]'>
                <div className="flex items-center justify-end w-[inherit]">
                    <Button variant="outline" size="icon" onClick={logout}>
                        <LogOut />
                    </Button>
                </div>
            </Header>
            <Main>
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-2xl font-bold">S3 File Manager</h1>
                    <div className="flex items-center gap-2">
                        {isUserScreenshotsFolder && (
                            <Button
                                variant="default"
                                size="sm"
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                                onClick={handleDetectIdenticalScreenshots}
                                disabled={isDetecting}
                            >
                                <Search className="w-4 h-4" />
                                {isDetecting ? 'Detecting...' : 'Detect Identical Screenshots'}
                            </Button>
                        )}
                        {currentFolder && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                                onClick={handleBackClick}
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </Button>
                        )}
                    </div>
                </div>

                <div className="mb-4">
                    <h2 className="text-xl font-semibold mb-2">Folders</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {loadingFoldes ? (
                            // Loading skeleton
                            Array.from({ length: 8 }).map((_, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200 animate-pulse"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="flex flex-col min-w-0">
                                            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            folders.map((folder) => {
                                const user = usersData?.users.find((user) => user._id === folder.split('/').slice(-2, -1).join('/'));
                                return (
                                    <div
                                        key={folder}
                                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="flex flex-col min-w-0">
                                                <span className="font-medium text-gray-900 truncate">
                                                    {user ? user?.name : folder.split('/').slice(-2, -1).join('/')}
                                                </span>
                                                <span className="text-xs text-gray-500 truncate">
                                                    {folder.split('/').slice(-1).join('/')}
                                                </span>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="p-1.5 hover:bg-gray-100"
                                                    onClick={() => handleFolderClick(folder)}
                                                >
                                                    <EyeIcon className="w-4 h-4 text-gray-600" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="p-1.5 hover:bg-gray-100"
                                                    onClick={(e) => handleDeleteFolderClick(e, folder)}
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                        <CopyButton text={folder.split('/').slice(-1).join('/')} />
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-semibold">Files</h2>
                        {isUserScreenshotsFolder && sortedFiles.length > 0 && (
                            <div className="flex items-center gap-2">
                                {selectedFiles.size > 0 && (
                                    <>
                                        <span className="text-sm text-gray-600">
                                            {selectedFiles.size} selected
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleSelectAllFiles}
                                            className="text-xs"
                                        >
                                            {selectedFiles.size === sortedFiles.length ? 'Deselect All' : 'Select All'}
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handleCreateTicketFromFiles}
                                            disabled={selectedFiles.size === 0}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            <Ticket className="w-4 h-4 mr-2" />
                                            Create Ticket ({selectedFiles.size})
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {sortedFiles.map((file, index) => {
                            const isSelected = selectedFiles.has(file.url);
                            return (
                            <div
                                key={file.key}
                                className={`border-2 p-3 rounded-lg hover:shadow-lg transition-all bg-white relative group ${
                                    isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200'
                                }`}
                                onDoubleClick={() => handleImageClick(index)}
                            >
                                    <div 
                                        className="relative aspect-square mb-2 overflow-hidden rounded-md"
                                    >
                                        {isUserScreenshotsFolder && (
                                            <div className="absolute top-2 left-2 z-10">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => handleToggleFileSelection(file.url)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="bg-white border-2 border-gray-300 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                                                />
                                            </div>
                                        )}
                                        <img
                                            src={file.url}
                                            alt={file.key}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                                            onClick={() => handleImageClick(index)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">{file.key.split('/').pop()}</p>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center text-xs text-gray-500">
                                                <Clock className="w-3 h-3 mr-1" />
                                                <span>{format(new Date(file.lastModified), 'dd/MM/yyyy HH:mm')}</span>
                                            </div>
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {!isUserScreenshotsFolder && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCreateTicketClick(e, file);
                                                        }}
                                                        title="Create Ticket"
                                                    >
                                                        <Ticket className="h-4 w-4 text-blue-500" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(e, file.key);
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <AlertDialog open={!!fileToDelete} onOpenChange={() => setFileToDelete(null)}>
                    <AlertDialogContent className='bg-white'>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Confirm delete file</AlertDialogTitle>
                            <AlertDialogDescription>
                                You are sure to delete this file? This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <AlertDialog open={!!folderToDelete} onOpenChange={() => {
                    setFolderToDelete(null);
                    setConfirmFolderName('');
                }}>
                    <AlertDialogContent className='bg-white'>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Confirm delete folder</AlertDialogTitle>
                            <AlertDialogDescription>
                                To confirm deletion, please type the folder name: <span className="font-semibold">{folderToDelete?.split('/').slice(-2, -1).join('/')}</span>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="py-4">
                            <Input
                                value={confirmFolderName}
                                onChange={(e) => setConfirmFolderName(e.target.value)}
                                placeholder="Enter folder name to confirm"
                                className="w-full"
                            />
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleConfirmDeleteFolder}
                                className="bg-red-500 hover:bg-red-600"
                                disabled={confirmFolderName !== folderToDelete?.split('/').slice(-2, -1).join('/')}
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {showGallery && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 z-[70] flex items-center justify-center">
                        <div className="w-full max-w-4xl mx-4">
                            <style>
                                {`
                                    .image-gallery-slide {
                                        height: 75vh !important;
                                    }
                                    .image-gallery-slide img {
                                        height: 100% !important;
                                        object-fit: contain !important;
                                    }
                                    .image-gallery-thumbnails-container {
                                        height: 20vh !important;
                                    }
                                    .image-gallery-thumbnail {
                                        height: 100% !important;
                                    }
                                    .image-gallery-thumbnail img {
                                        height: 100% !important;
                                        object-fit: cover !important;
                                    }
                                `}
                            </style>
                            <ImageGallery
                                items={images}
                                startIndex={selectedImageIndex}
                                showPlayButton={false}
                                showFullscreenButton={true}
                                showThumbnails={true}
                                showNav={true}
                                showIndex={true}
                            />
                            <Button
                                className="absolute top-4 right-4 bg-white text-black hover:bg-gray-200"
                                onClick={() => setShowGallery(false)}
                            >
                                Đóng
                            </Button>
                        </div>
                    </div>
                )}

                <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
                    <DialogContent className="bg-white max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Create Ticket</DialogTitle>
                            <DialogDescription>
                                Enter the reason for this ticket. You can use the Polish button to improve the content using AI.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedFile && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Image Preview</label>
                                    <div className="border rounded-lg overflow-hidden">
                                        <img
                                            src={selectedFile.url}
                                            alt={selectedFile.key}
                                            className="w-full h-48 object-contain bg-gray-50"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label htmlFor="reason" className="text-sm font-medium">
                                    Reason *
                                </label>
                                <Textarea
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Enter the reason for this ticket..."
                                    className="min-h-[120px]"
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                onClick={handlePolishContent}
                                disabled={isPolishing || !reason.trim()}
                            >
                                {isPolishing ? 'Polishing...' : 'Polish Content using OpenAI'}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowTicketModal(false);
                                        setSelectedFile(null);
                                        setReason('');
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmitTicket}
                                    disabled={isSubmitting || !reason.trim()}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit to Tickets Slack Channel'}
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={showIdenticalModal} onOpenChange={setShowIdenticalModal}>
                    <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Identical Screenshots Detection Results</DialogTitle>
                            <DialogDescription>
                                {identicalResults && (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-600">
                                            Found <span className="font-semibold text-red-600">{identicalResults.totalIdenticalGroups}</span> group(s) of identical screenshots
                                            with <span className="font-semibold text-red-600">{identicalResults.totalDuplicateFiles}</span> duplicate file(s).
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            These screenshots are 100% identical and may indicate time tracking without actual work.
                                        </p>
                                    </div>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {identicalResults && identicalResults.groups.length > 0 ? (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-600 mb-1">
                                                Select screenshots to create a ticket ({selectedScreenshots.size} selected)
                                            </p>
                                            {(() => {
                                                const selectedByGroup = getSelectedCountByGroup();
                                                const hasSelections = Object.keys(selectedByGroup).length > 0;
                                                return hasSelections ? (
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        {Object.entries(selectedByGroup).map(([groupIdx, count]) => {
                                                            const group = identicalResults.groups[parseInt(groupIdx)];
                                                            return (
                                                                <span
                                                                    key={groupIdx}
                                                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full"
                                                                >
                                                                    Group {parseInt(groupIdx) + 1}: {count}/{group.files.length}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handleCreateTicketFromIdentical}
                                            disabled={selectedScreenshots.size === 0}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            <Ticket className="w-4 h-4 mr-2" />
                                            Create Ticket ({selectedScreenshots.size})
                                        </Button>
                                    </div>
                                    {identicalResults.groups.map((group, groupIndex) => {
                                        const groupUrls = group.files.map(f => f.url);
                                        const selectedInGroup = group.files.filter(f => selectedScreenshots.has(f.url)).length;
                                        const allSelected = groupUrls.every(url => selectedScreenshots.has(url));
                                        return (
                                        <div key={group.hash} className="border rounded-lg p-4 bg-red-50">
                                            <div className="mb-3 flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-semibold text-red-700">
                                                        Group {groupIndex + 1} - {group.files.length} identical screenshot(s)
                                                        {selectedInGroup > 0 && (
                                                            <span className="ml-2 text-sm text-blue-600">
                                                                ({selectedInGroup} selected)
                                                            </span>
                                                        )}
                                                    </h3>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <p className="text-xs text-gray-500">Hash: {group.hash.substring(0, 16)}...</p>
                                                        {(() => {
                                                            const duration = calculateGroupDuration(group.files);
                                                            return duration ? (
                                                                <span className="text-xs font-medium text-orange-600">
                                                                    Duration: {duration}
                                                                </span>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleSelectAllInGroup(group.files)}
                                                    className="text-xs"
                                                >
                                                    {allSelected ? 'Deselect All' : 'Select All'}
                                                </Button>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                {group.files.map((file, fileIndex) => {
                                                    const isSelected = selectedScreenshots.has(file.url);
                                                    return (
                                                        <div
                                                            key={file.key}
                                                            className={`border-2 rounded-lg overflow-hidden bg-white cursor-pointer hover:shadow-md transition-all ${
                                                                isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200'
                                                            }`}
                                                            onClick={() => handleOpenImageFromIdentical(file.key)}
                                                        >
                                                            <div className="relative aspect-square overflow-hidden">
                                                                <div className="absolute top-2 left-2 z-10">
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onCheckedChange={() => handleToggleScreenshotSelection(file.url)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="bg-white border-2 border-gray-300 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                                                                    />
                                                                </div>
                                                                <img
                                                                    src={file.url}
                                                                    alt={file.key}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <div className="absolute bottom-2 right-2 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded font-semibold">
                                                                    G{groupIndex + 1}
                                                                </div>
                                                                {fileIndex === 0 && (
                                                                    <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                                                                        Original
                                                                    </div>
                                                                )}
                                                                {fileIndex > 0 && (
                                                                    <div className="absolute top-1 left-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded">
                                                                        Duplicate
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="p-2">
                                                                <p className="text-xs font-medium text-gray-900 truncate">
                                                                    {file.key.split('/').pop()}
                                                                </p>
                                                                <div className="flex items-center text-xs text-gray-500 mt-1">
                                                                    <Clock className="w-3 h-3 mr-1" />
                                                                    <span>{format(new Date(file.lastModified), 'dd/MM/yyyy HH:mm')}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No identical screenshots found.</p>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowIdenticalModal(false);
                                    // Keep identicalResults in state, only clear selectedScreenshots
                                    setSelectedScreenshots(new Set());
                                }}
                            >
                                Close
                            </Button>
                            {identicalResults && identicalResults.groups.length > 0 && (
                                <Button
                                    variant="default"
                                    onClick={handleCreateTicketFromIdentical}
                                    disabled={selectedScreenshots.size === 0}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    <Ticket className="w-4 h-4 mr-2" />
                                    Create Ticket ({selectedScreenshots.size})
                                </Button>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={showTicketModalFromIdentical} onOpenChange={setShowTicketModalFromIdentical}>
                    <DialogContent className="bg-white max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Create Ticket with Multiple Screenshots</DialogTitle>
                            <DialogDescription>
                                Enter the reason for this ticket. You can use the Polish button to improve the content using AI.
                                {selectedScreenshots.size > 0 && (
                                    <span className="block mt-2 text-sm font-medium text-blue-600">
                                        {selectedScreenshots.size} screenshot(s) selected
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedScreenshots.size > 0 && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Selected Screenshots Preview</label>
                                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                        {Array.from(selectedScreenshots).map((url, index) => {
                                            // Find which group this screenshot belongs to
                                            const groupInfo = identicalResults?.groups.findIndex(group => 
                                                group.files.some(f => f.url === url)
                                            );
                                            return (
                                                <div key={index} className="relative aspect-square border rounded overflow-hidden">
                                                    <img
                                                        src={url}
                                                        alt={`Screenshot ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {groupInfo !== undefined && groupInfo !== -1 && (
                                                        <div className="absolute top-1 right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded font-semibold">
                                                            G{groupInfo + 1}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {(() => {
                                        const selectedByGroup = getSelectedCountByGroup();
                                        return Object.keys(selectedByGroup).length > 0 ? (
                                            <div className="text-xs text-gray-600 mt-2">
                                                <span className="font-medium">Selected by group: </span>
                                                {Object.entries(selectedByGroup).map(([groupIdx, count], idx) => (
                                                    <span key={groupIdx}>
                                                        {idx > 0 && ', '}
                                                        Group {parseInt(groupIdx) + 1} ({count})
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                            <div className="space-y-2">
                                <label htmlFor="reason-identical" className="text-sm font-medium">
                                    Reason *
                                </label>
                                <Textarea
                                    id="reason-identical"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Enter the reason for this ticket..."
                                    className="min-h-[120px]"
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                onClick={handlePolishContent}
                                disabled={isPolishing || !reason.trim()}
                            >
                                {isPolishing ? 'Polishing...' : 'Polish Content using OpenAI'}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowTicketModalFromIdentical(false);
                                        setReason('');
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmitTicketFromIdentical}
                                    disabled={isSubmitting || !reason.trim() || selectedScreenshots.size === 0}
                                >
                                    {isSubmitting ? 'Submitting...' : `Submit to Tickets Slack Channel (${selectedScreenshots.size} screenshots)`}
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={showTicketModalFromFiles} onOpenChange={setShowTicketModalFromFiles}>
                    <DialogContent className="bg-white max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Create Ticket with Selected Screenshots</DialogTitle>
                            <DialogDescription>
                                Enter the reason for this ticket. You can use the Polish button to improve the content using AI.
                                {selectedFiles.size > 0 && (
                                    <span className="block mt-2 text-sm font-medium text-blue-600">
                                        {selectedFiles.size} screenshot(s) selected
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedFiles.size > 0 && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Selected Screenshots Preview</label>
                                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                        {Array.from(selectedFiles).map((url, index) => {
                                            const file = sortedFiles.find(f => f.url === url);
                                            return (
                                                <div key={index} className="relative aspect-square border rounded overflow-hidden">
                                                    <img
                                                        src={url}
                                                        alt={`Screenshot ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {file && (
                                                        <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded truncate">
                                                            {file.key.split('/').pop()}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label htmlFor="reason-files" className="text-sm font-medium">
                                    Reason *
                                </label>
                                <Textarea
                                    id="reason-files"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Enter the reason for this ticket..."
                                    className="min-h-[120px]"
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                onClick={handlePolishContent}
                                disabled={isPolishing || !reason.trim()}
                            >
                                {isPolishing ? 'Polishing...' : 'Polish Content using OpenAI'}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowTicketModalFromFiles(false);
                                        setReason('');
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmitTicketFromFiles}
                                    disabled={isSubmitting || !reason.trim() || selectedFiles.size === 0}
                                >
                                    {isSubmitting ? 'Submitting...' : `Submit to Tickets Slack Channel (${selectedFiles.size} screenshots)`}
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Main>
        </>
    );
};

export default S3File;
